import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {categories} from '../data/poses';

const {FiCamera,FiUsers,FiStar,FiTrendingUp,FiBriefcase,FiHeart,FiZap,FiImage,FiGrid,FiChevronRight}=FiIcons;

const categoryIcons={
  business: FiBriefcase,
  senior: FiStar,
  couple: FiHeart,
  creative: FiZap,
  lifestyle: FiCamera,
  fitness: FiTrendingUp,
  artistic: FiImage,
  branding: FiBriefcase
};

const categoryPalette={
  business: '#0F0F0F',
  senior: '#1C1C1C',
  couple: '#2E2A2B',
  creative: '#3B2F2F',
  lifestyle: '#4B3B3F',
  fitness: '#5C4B51',
  artistic: '#6D5C5F',
  branding: '#7E6D6F'
};

function Home() {
  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <motion.div initial={{y: -20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.8}} className="text-center mb-12" >
        <div className="flex justify-center items-center mb-6">
          <video 
            src="https://filedn.com/lujmEiF7ALfJ9rDUrehIfEz/MatchaDesign_Posefolio-Logo.mp4" 
            className="h-32 md:h-56 w-auto max-w-full" 
            autoPlay muted loop playsInline 
          />
        </div>
        <p className="text-lg text-slate-400 max-w-xl mx-auto font-light tracking-wide px-4">
          The definitive pose reference library for the modern photographer.
        </p>
      </motion.div>

      {/* Optimized Category Grid */}
      <motion.div initial={{opacity: 0}} animate={{opacity: 1}} transition={{delay: 0.2}} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 px-2 mb-20" >
        {categories.map((category,index)=> (
          <motion.div key={category.id} whileHover={{y: -4}} transition={{type: 'spring', stiffness: 300, damping: 25}}>
            <Link to={`/category/${category.id}`} className="block h-full group">
              <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden h-full flex flex-col border border-slate-100">
                <div 
                  className="p-5 sm:p-8 text-white flex-grow flex flex-col justify-between min-h-[140px] sm:min-h-[220px] relative overflow-hidden" 
                  style={{backgroundColor: categoryPalette[category.id]}}
                >
                  {/* Subtle Grain Overlay */}
                  <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] pointer-events-none"></div>
                  
                  {/* Icon Container - Hidden on Mobile to save space */}
                  <div className="relative z-10 hidden sm:flex items-center justify-between">
                    <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                      <SafeIcon icon={categoryIcons[category.id]} className="text-xl" />
                    </div>
                  </div>
                  
                  <div className="relative z-10">
                    <h3 className="text-2xl sm:text-3xl font-serif mb-1 sm:mb-2 leading-tight">{category.name}</h3>
                    <p className="text-white/60 text-[10px] sm:text-xs leading-relaxed font-light line-clamp-2 sm:line-clamp-none">
                      {category.description}
                    </p>
                  </div>
                </div>
                {/* Compact Footer */}
                <div className="p-4 sm:p-5 bg-white flex items-center justify-between border-t border-slate-50">
                  <span className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] group-hover:text-brand-primary transition-colors">View Collection</span>
                  <SafeIcon icon={FiChevronRight} className="text-sm text-slate-300 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      {/* Redesigned Premium Feature Section */}
      <section className="py-16 border-t border-slate-200">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-serif text-slate-900 mb-4">The PÓSEFLIO Standard</h2>
          <div className="w-12 h-0.5 bg-brand-primary mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 px-4">
          {/* Curated */}
          <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once: true}} className="flex flex-col items-center text-center group">
            <div className="w-14 h-14 rounded-xl bg-slate-50 flex items-center justify-center mb-5 group-hover:bg-brand-primary group-hover:text-white transition-all duration-500 shadow-sm">
              <SafeIcon icon={FiCamera} className="text-2xl" />
            </div>
            <h3 className="text-xl font-serif mb-3 text-slate-900">Curated Selection</h3>
            <p className="text-xs text-slate-500 leading-relaxed max-w-xs font-light">
              160+ masterfully selected poses tailored for impact and elegance. Each reference is hand-picked to ensure your subjects look their absolute best.
            </p>
          </motion.div>

          {/* Direction */}
          <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once: true}} transition={{delay: 0.1}} className="flex flex-col items-center text-center group">
            <div className="w-14 h-14 rounded-xl bg-slate-50 flex items-center justify-center mb-5 group-hover:bg-brand-primary group-hover:text-white transition-all duration-500 shadow-sm">
              <SafeIcon icon={FiUsers} className="text-2xl" />
            </div>
            <h3 className="text-xl font-serif mb-3 text-slate-900">Artful Direction</h3>
            <p className="text-xs text-slate-500 leading-relaxed max-w-xs font-light">
              Clear, verbal scripts designed to spark natural movement. Guide your subjects with confidence using our proven professional cues.
            </p>
          </motion.div>

          {/* Premium */}
          <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once: true}} transition={{delay: 0.2}} className="flex flex-col items-center text-center group">
            <div className="w-14 h-14 rounded-xl bg-slate-50 flex items-center justify-center mb-5 group-hover:bg-brand-primary group-hover:text-white transition-all duration-500 shadow-sm">
              <SafeIcon icon={FiStar} className="text-2xl" />
            </div>
            <h3 className="text-xl font-serif mb-3 text-slate-900">Premium Quality</h3>
            <p className="text-xs text-slate-500 leading-relaxed max-w-xs font-light">
              High-definition visual references for elite portraiture. Elevate your portfolio with a standard of excellence trusted by top-tier photographers.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Home;